import { Divider, Paper, Stack, Typography } from '@mui/material';
import React from 'react';
import type { MessageDescriptor } from 'react-intl';
import { defineMessages, useIntl } from 'react-intl';

import { ListItemToggle } from '@components/ListItem';

import type { OrgFeatureModel } from 'generated/types';

const messages = defineMessages({
  access: {
    id: 'orgFeatures.category.access',
    defaultMessage: 'Access',
  },
  recordsImaging: {
    id: 'orgFeatures.category.recordsImaging',
    defaultMessage: 'Records & Imaging',
  },
  careEngagement: {
    id: 'orgFeatures.category.careEngagement',
    defaultMessage: 'Care & Engagement',
  },
  insightsBrand: {
    id: 'orgFeatures.category.insightsBrand',
    defaultMessage: 'Insights & Brand',
  },
  other: {
    id: 'orgFeatures.category.other',
    defaultMessage: 'Other',
  },
  enabledCount: {
    id: 'orgFeatures.enabledCount',
    defaultMessage: '{count} of {total} enabled',
  },
  patientFeature: {
    id: 'orgFeatures.patientFeature',
    defaultMessage: 'Patient feature: {name}',
  },
});

/**
 * Display order of the category headings. `category` is a free-form string on
 * the wire, and the backend hands features over grouped into one contiguous run
 * per category but states that the order of the runs is not significant — the
 * client picks which heading comes first. This list is that choice.
 */
const CATEGORY_ORDER = [
  'access',
  'records_imaging',
  'care_engagement',
  'insights_brand',
] as const;

type KnownCategory = (typeof CATEGORY_ORDER)[number];

const CATEGORY_LABELS: Record<KnownCategory, MessageDescriptor> = {
  access: messages.access,
  records_imaging: messages.recordsImaging,
  care_engagement: messages.careEngagement,
  insights_brand: messages.insightsBrand,
};

/** Bucket a newly seeded category lands in until it is given a heading here. */
const OTHER_CATEGORY = 'other';

type CategoryKey = KnownCategory | typeof OTHER_CATEGORY;

function categoryKey(category: string): CategoryKey {
  return CATEGORY_ORDER.includes(category as KnownCategory)
    ? (category as KnownCategory)
    : OTHER_CATEGORY;
}

type FeatureOrder = Pick<OrgFeatureModel, 'category' | 'display_order'>;

function categoryRank(category: string): number {
  const index = CATEGORY_ORDER.indexOf(category as KnownCategory);
  return index === -1 ? CATEGORY_ORDER.length : index;
}

/**
 * Catalog order: category as `CATEGORY_ORDER` lists them, then `display_order`
 * within it. Exported because the patient plan editor offers a subset of these
 * same features and has to read in the same order as the toggle list —
 * `getEligibleFeatures` in `@components/PatientPlans` sorts with it.
 *
 * Every category without a heading shares one rank, so the raw category string
 * breaks that tie and keeps each such category contiguous; `display_order`
 * restarts per category, so ordering on it alone would interleave them. Known
 * categories are unaffected — their ranks differ, so the string comparison
 * never decides anything.
 */
export function compareFeaturesByCategory(
  a: FeatureOrder,
  b: FeatureOrder
): number {
  return (
    categoryRank(a.category) - categoryRank(b.category) ||
    (a.category < b.category ? -1 : a.category > b.category ? 1 : 0) ||
    a.display_order - b.display_order
  );
}

/**
 * Group features under their heading, in catalog order, keeping any feature
 * whose category has no heading yet rather than dropping it.
 */
function groupByCategory(features: OrgFeatureModel[]) {
  const groups = new Map<CategoryKey, OrgFeatureModel[]>();

  for (const feature of features) {
    const key = categoryKey(feature.category);
    groups.set(key, [...(groups.get(key) ?? []), feature]);
  }

  const keys: CategoryKey[] = [...CATEGORY_ORDER, OTHER_CATEGORY];

  return keys
    .filter((key) => groups.has(key))
    .map((key) => ({
      key,
      label: key === OTHER_CATEGORY ? messages.other : CATEGORY_LABELS[key],
      features: [...(groups.get(key) as OrgFeatureModel[])].sort(
        compareFeaturesByCategory
      ),
    }));
}

interface OrgFeatureRowProps {
  feature: OrgFeatureModel;
  enabled: boolean;
  onToggle: (featureId: string, enabled: boolean) => void;
  disabled?: boolean;
}

function OrgFeatureRow({
  feature,
  enabled,
  onToggle,
  disabled,
}: OrgFeatureRowProps) {
  const { formatMessage } = useIntl();

  return (
    <ListItemToggle
      data-testid={`org-feature-row-${feature.name}`}
      disabled={disabled}
      enabled={enabled}
      onToggle={(checked) => onToggle(feature.feature_id, checked)}
      primary_text={feature.display_name}
      secondary_text={feature.description ?? undefined}
      // Static: the row states which patient feature it carries whether it is
      // on or off, so the line never pops in and the row height never shifts
      // under the pointer as the toggle is flipped (VEG-828).
      tertiary_text={
        feature.patient_display_name
          ? formatMessage(messages.patientFeature, {
              name: feature.patient_display_name,
            })
          : undefined
      }
      variant="row"
    />
  );
}

interface OrgFeatureToggleListProps {
  features: OrgFeatureModel[];
  enabledIds: string[];
  onToggle: (featureId: string, enabled: boolean) => void;
  disabled?: boolean;
}

export default function OrgFeatureToggleList({
  features,
  enabledIds,
  onToggle,
  disabled,
}: OrgFeatureToggleListProps) {
  const { formatMessage } = useIntl();
  const instanceId = React.useId();
  const groups = groupByCategory(features);

  return (
    <Stack spacing={2.75}>
      {groups.map((group) => {
        const count = group.features.filter(({ feature_id }) =>
          enabledIds.includes(feature_id)
        ).length;
        const headingId = `${instanceId}-${group.key}-heading`;

        return (
          <Stack
            data-testid={`org-feature-category-${group.key}`}
            key={group.key}
            spacing={1}
          >
            <Stack
              alignItems="center"
              direction="row"
              justifyContent="space-between"
            >
              <Typography
                color="text.secondary"
                component="h3"
                id={headingId}
                variant="overline"
              >
                {formatMessage(group.label)}
              </Typography>
              <Typography
                color="text.secondary"
                data-testid={`org-feature-count-${group.key}`}
                variant="body2"
              >
                {formatMessage(messages.enabledCount, {
                  count,
                  total: group.features.length,
                })}
              </Typography>
            </Stack>

            <Paper
              aria-labelledby={headingId}
              role="group"
              // `MuiPaper.styleOverrides.root` in src/theme/index.ts sets
              // `border: none` and a 16px radius, which lands after the
              // `outlined` variant and cancels it. Not redundant — do not remove.
              sx={{
                border: 1,
                borderColor: 'divider',
                borderRadius: 2,
              }}
              variant="outlined"
            >
              {group.features.map((feature, index) => (
                <React.Fragment key={feature.feature_id}>
                  {index > 0 && <Divider />}
                  <OrgFeatureRow
                    disabled={disabled}
                    enabled={enabledIds.includes(feature.feature_id)}
                    feature={feature}
                    onToggle={onToggle}
                  />
                </React.Fragment>
              ))}
            </Paper>
          </Stack>
        );
      })}
    </Stack>
  );
}
